import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TableDatasource, DataTypes } from '@gamelearn/skeletor/models';
import { EntityQuestion } from 'core/components';
import {
  AgentsService,
  ListHelperService,
  ProfileService,
  StoreService,
  CountriesService,
  LocalesService,
  ClientsService,
  DistributorsService,
  PopUpService,
  NotificationsService
} from 'core/services';

@Injectable()
export class AgentsListService {
  userRole: any;
  clientQuestion: EntityQuestion;
  partnerQuestion: EntityQuestion;
  searchClientsComponent: any;
  searchPartnersComponent: any;

  constructor(
    public agentsService: AgentsService,
    public clientsService: ClientsService,
    public distributorsService: DistributorsService,
    public translate: TranslateService,
    public profileService: ProfileService,
    public listHelper: ListHelperService,
    public countriesService: CountriesService,
    public locales: LocalesService,
    private popupService: PopUpService,
    private notificationsService: NotificationsService
  ) {
    profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
    this.initFilters();
  }

  initFilters() {
    this.clientQuestion = new EntityQuestion({
      key: 'clientIds',
      dataProvider: this.clientsService
    });
    this.partnerQuestion = new EntityQuestion({
      key: 'distributorId',
      dataProvider: this.distributorsService
    });

    this.clientQuestion.getOptions$().subscribe((options) => this.setSearchClientsOptions(options));
    this.partnerQuestion.getOptions$().subscribe((options) => this.setSearchPartnersOptions(options));
  }

  async getDatasource(listId, options: any = {}) {
    if (options.filters) {
      if (options.filters.clientIds) {
        this.clientQuestion.preload(options.filters.clientIds);
      }
      if (options.filters.distributorId) {
        this.partnerQuestion.preload(options.filters.distributorId);
      }
    }
    const dataSource = new TableDatasource({
      sort: 'name',
      aggregates: ['clients'],
      populate: 'partner',
      columns: this.listHelper.filterColumns(this.getColumns(), options),
      load: (props) => this.fetch(props, dataSource),
      storeState: {
        enabled: true,
        key: `${listId}_${this.userRole.name}`
      },
      ...options
    });
    await dataSource.refreshData();
    StoreService.create(listId, dataSource);
    return dataSource;
  }

  fetch({ page, limit, sort, search, filters, aggregates, populate }, dataSource: TableDatasource) {
    const { clientIds, distributorId, ...$filterValues } = filters;
    const $filter = this.listHelper.buildFilterString($filterValues, dataSource, this.getCustomFilters());

    const paginationOptions = {
      page,
      limit,
      sort,
      search,
      aggregates,
      populate,
      clientIds,
      distributorId,
      $filter
    };
    return this.agentsService.fetch(paginationOptions).then((response) => response);
  }

  getColumns() {
    const columns = [
      {
        source: 'name',
        label: this.translate.instant('agents.header.name'),
        sortable: true,
        style: { width: '200px' },
        cellRenderer: (renderer, value, agent) =>
          renderer`<div class="ellipsis">${this.listHelper.getRouterLinkRenderer(
            renderer,
            `/agents/${agent.id}`,
            value
          )}</div>`
      },
      {
        source: 'surname',
        label: this.translate.instant('nouns.surname'),
        sortable: true,
        style: { width: '200px' }
      },
      {
        source: 'email',
        label: this.translate.instant('nouns.email'),
        style: { width: '200px' },
        sortable: true
      },
      {
        source: 'countryId',
        label: this.translate.instant('nouns.country'),
        style: { width: '150px' },
        valueParser: (value) => this.listHelper.parseCountryName(value),
        sortable: true,
        searchRenderer: (renderer, value, callback) => {
          const options = this.countriesService.getOptions();
          options.unshift({ key: 'any', value: this.translate.instant('properties.any') });
          return renderer`
          <gl-select
          small
          .value="${value || 'any'}"
          .options="${options}"
          @onChange="${(ev) => callback(ev.detail.value !== 'any' ? ev.detail.value : null)}">
          </gl-select>
          `;
        }
      },
      {
        source: 'lang',
        label: this.translate.instant('nouns.lang'),
        sortable: false,
        style: { width: '120px' },
        valueParser: (value) => this.locales.getLocaleName(value),
        searchRenderer: (renderer, value, callback) => {
          const localesOptions = this.locales.getDefaultOptions();
          localesOptions.unshift({ key: 'any', value: this.translate.instant('properties.any') });
          return renderer`
          <gl-select
          small
          .value="${value || 'any'}"
          .options="${localesOptions}"
          @onChange="${(ev) => callback(ev.detail.value !== 'any' ? ev.detail.value : null)}">
          </gl-select>
          `;
        }
      },
      {
        source: 'role',
        label: this.translate.instant('nouns.role'),
        sortable: true,
        style: { width: '150px' },
        valueParser: (value, agent) => (value ? this.translate.instant('agents.roles.' + value) : ''),
        searchRenderer: (renderer, value, callback) => {
          const rolesOptions = this.profileService.getRolesOptions(false, false, true);
          rolesOptions.unshift({ key: 'any', value: this.translate.instant('properties.any') });
          return renderer`
          <gl-select
          small
          .value="${value || 'any'}"
          .options="${rolesOptions}"
          @onChange="${(ev) => callback(ev.detail.value !== 'any' ? ev.detail.value : null)}">
          </gl-select>
          `;
        }
      },
      {
        source: 'ownerConfig.owner',
        label: this.translate.instant('nouns.isOwner'),
        alignment: 'center',
        dataType: DataTypes.boolean,
        sortable: true,
        style: { width: '120px' }
      },
      {
        source: 'isCreator',
        label: this.translate.instant('nouns.isCreator'),
        alignment: 'center',
        style: { width: '125px' },
        dataType: DataTypes.boolean,
        sortable: true
      },
      {
        source: 'clientIds',
        label: this.translate.instant('nouns.client'),
        sortable: true,
        style: { width: '200px' },
        valueParser: (value, agent) => {
          const { clients = [] } = agent;
          if (clients && clients.length) {
            return clients.map((client) => client.name);
          }
          return '';
        },
        cellRenderer: (renderer, value) => {
          const colorTrigger = ['#e1e4e8'];
          const colorDefault = ['#c3ccdc'];
          if (value && value.length) {
            const [firstAgent, ...restAgents] = value;
            return renderer`
              <div class="tags">
                ${renderer`<gl-tag .colors="${colorDefault}" label="${firstAgent}"></gl-tag>`}
                ${
                  restAgents.length === 0
                    ? ''
                    : renderer`
                      <gl-tooltip posLeft light>
                        <gl-tag slot="trigger" .colors="${colorTrigger}" label="+${restAgents.length}"></gl-tag>
                        ${restAgents.map((item) => renderer`<gl-tag label="${item}"></gl-tag>`)}
                      </gl-tooltip>
                    `
                }
                </div>
                `;
          }
          return '';
        },
        searchRenderer: (renderer, value, callback) => {
          return renderer`
            <gl-input-tag
              .optionLabel="${this.translate.instant('actions.select')}"
              .options="${this.clientQuestion.options}"
              .createTags="${false}"
              .value="${value}"
              tagsLimit=""
              small
              @onKeyup="${(ev) => this.onSearchClients(ev)}"
              @onChange="${(ev) => callback(ev.detail.value)}"
            >
            </gl-input-tag>
          `;
        }
      },
      {
        source: 'distributorId',
        label: this.translate.instant('nouns.distributor'),
        sortable: true,
        visible: false,
        style: { width: '200px' },
        valueParser: (value, agent) => {
          if (agent.partner) {
            return agent.partner.name;
          }
          return '';
        },
        searchRenderer: (renderer, value, callback) => {
          return renderer`
            <gl-input-tag
              .optionLabel="${this.translate.instant('actions.select')}"
              .options="${this.partnerQuestion.options}"
              .createTags="${false}"
              .value="${value}"
              tagsLimit="1"
              small
              @onKeyup="${(ev) => this.onSearchPartners(ev)}"
              @onChange="${(ev) => callback(ev.detail.value)}"
            >
            </gl-input-tag>
          `;
        }
      },
      this.profileService.canExecute('agents.resendWelcomeEmail')
        ? {
            alignment: 'right',
            showInColumnChooser: false,
            style: { width: '75px' },
            cellRenderer: (renderer, value, agent) => {
              if (agent.blocked) {
                return '';
              }

              return renderer`
            <gl-context-menu medium>
              <gl-button slot="target" id="${agent.id}" icon icon-center="glicon-dots"></gl-button>
              
              ${renderer`<gl-menu-item text="${this.translate.instant('actions.resendInvite')}" @click="${() =>
                this.listHelper.run(() => this.resendWelcomeNotification(agent))}"></gl-menu-item>`}

            </gl-context-menu>`;
            }
          }
        : null
    ];

    return columns.filter((column) => column);
  }

  onSearchClients(ev) {
    this.searchClientsComponent = ev.target;
    this.clientQuestion.onKeyup(ev.detail.value);
  }
  setSearchClientsOptions(options) {
    if (this.searchClientsComponent) {
      this.searchClientsComponent.options = options;
    }
  }
  onSearchPartners(ev) {
    this.searchPartnersComponent = ev.target;
    this.partnerQuestion.onKeyup(ev.detail.value);
  }
  setSearchPartnersOptions(options) {
    if (this.searchPartnersComponent) {
      this.searchPartnersComponent.options = options;
    }
  }

  getCustomFilters() {
    return {
      'ownerConfig.owner': (filterBuilder, value) => {
        return filterBuilder.ge('ownerConfig/owner', value).le('ownerConfig/owner', value);
      },
      role: (filterBuilder, value) => {
        return filterBuilder.eq('role', value);
      }
    };
  }

  private resendWelcomeNotification(agent) {
    this.popupService
      .showConfirm({
        title: this.translate.instant('agents.resendWelcomeNotification', { email: agent.email }),
        confirmButtonText: this.translate.instant('actions.accept'),
        type: 'error',
        class: 'center',
        size: 'x-small'
      })
      .then(() => {
        this.notificationsService.sendWelcomeAdminNotification(agent);
      })
      .catch(() => {});
  }
}
